import React, { useRef, useState, CSSProperties } from 'react';
import cx from 'classnames';
import { ToolTipProps, AppearsFromValues } from './ToolTip.types';
import Icon from '../../atoms/Icon';
import styles from './ToolTip.module.scss';
import { StyleSheetColor } from '../../shared/types/styleSheetColors.types';
import { TextColor } from '../../shared/types/textColor.types';
import Button from '../../atoms/Button';
import { ConditionBlock, ElementBlock, StringBlock } from '../../utils/Block';
import IconProps from '../../atoms/Icon/Icon.types';

const closeIcon: IconProps = {
  width: '14',
  height: '14',
  icon: 'close-tooltip',
  viewBox: '0 0 14 14',
  fillRule: 'evenodd',
};

const questionIcon: IconProps = {
  width: '1.625rem',
  height: '1.625rem',
  icon: 'question-tooltip-circle',
  viewBox: '0 0 24 24',
  fillRule: 'evenodd',
};

const ToolTip = ({
  appearsFrom = AppearsFromValues.Below,
  text,
  children,
  color = TextColor.Primary,
  fillColor = StyleSheetColor.Primary,
  className,
}: ToolTipProps) => {
  const popOutRef = useRef(null);
  const [height, setHeight] = useState(0);
  const [width, setWidth] = useState(0);
  const [showPopout, setShowPopout] = useState(false);

  const isIcon = !text;

  const showPopoutHandler = () => {
    if (popOutRef.current !== null) {
      const height = popOutRef.current.offsetHeight;
      const width = popOutRef.current.offsetWidth;
      setHeight(height);
      setWidth(width);
    }

    setShowPopout(true);
  };

  const hidePopoutHandler = () => setShowPopout(false);

  const marginObj = {
    '--tooltip-margin-left': `${-(width / 2)}px`, // using half of the width centers the tooltip,
    '--tooltip-margin-top': `${-(height / 2)}px`, // using half of the height centers the tooltip
  } as CSSProperties;

  const popOut = (
    <span
      data-testid='Popout'
      className={cx(styles.tooltipPopout, styles[appearsFrom], {
        [styles.showPopout]: showPopout,
      })}
      ref={popOutRef}
      style={{
        ...marginObj,
      }}
      role='tooltip'>
      <div className={styles.iconContainer}>
        <Button
          variant='icon'
          className={cx(styles.noButton, styles.closeWrapper)}
          onClick={() => hidePopoutHandler()}
          aria-label='close tooltip'
          name='tooltip button'>
          <Icon {...closeIcon} fillColor={fillColor} />
        </Button>
      </div>
      <div className={styles.popOutChildren}>{children}</div>
    </span>
  );

  return (
    <div data-testid='ToolTip' className={cx(styles.tooltip, className)}>
      <div
        className={cx({
          [styles.icon]: isIcon,
          [styles.underlineText]: !isIcon,
        })}>
        <div
          className={cx(styles.text, styles.noButton, styles[color])}
          onMouseEnter={showPopoutHandler}
          onMouseLeave={hidePopoutHandler}>
          <ConditionBlock>
            <StringBlock value={text}>{text}</StringBlock>
            <ElementBlock value={text}>{text}</ElementBlock>
          </ConditionBlock>
        </div>
        <div
          className={cx({ [styles.iconWrapper]: isIcon })}
          onMouseEnter={showPopoutHandler}
          onMouseLeave={hidePopoutHandler}>
          <Button
            name='tooltip button'
            variant='icon'
            aria-label='open tooltip'
            className={cx(styles.hiddenButton, {
              [styles.hiddenIconButton]: isIcon,
              [styles.hiddenTextButton]: !isIcon,
            })}
            onClick={showPopoutHandler}
          />
          {isIcon && (
            <div>
              <Icon {...questionIcon} fillColor={fillColor} />
            </div>
          )}
          {popOut}
        </div>
      </div>
    </div>
  );
};

export default ToolTip;
